/**
 * Display formatting.
 *
 * Dates arrive as ISO strings from the API and are rendered in the viewer's
 * locale and timezone. Anything financial is formatted through
 * `@accounting/shared`'s `format`, not here — money has one formatter in this
 * codebase and it lives with the money type. `formatMoney` below is a wrapper
 * around it, not a second implementation.
 */
import { format, minor } from '@accounting/shared'

const DATE_TIME = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
})

export function formatDateTime(value: string | null | undefined, fallback = '—'): string {
  if (!value) return fallback
  const parsed = new Date(value)
  return Number.isNaN(parsed.valueOf()) ? fallback : DATE_TIME.format(parsed)
}

const DATE_ONLY = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' })

/**
 * A date with no time of day.
 *
 * For values whose precision genuinely stops at the day — a projected depletion
 * date derived from a three-month average, an all-day court entry. Rendering
 * either with a clock time implies the projection knows the hour, which is
 * exactly the kind of false precision a finance screen gets quoted on.
 */
export function formatDate(value: string | null | undefined, fallback = '—'): string {
  if (!value) return fallback
  const parsed = new Date(value)
  return Number.isNaN(parsed.valueOf()) ? fallback : DATE_ONLY.format(parsed)
}

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 60 * 60_000],
  ['month', 30 * 24 * 60 * 60_000],
  ['day', 24 * 60 * 60_000],
  ['hour', 60 * 60_000],
  ['minute', 60_000],
]

/**
 * "3 hours ago" for freshness, where the absolute time is less useful than the
 * gap. Used for sync timestamps: whether a figure is an hour or a week old is
 * the question being asked.
 */
export function formatRelative(value: string | null | undefined, fallback = 'never'): string {
  if (!value) return fallback
  const parsed = new Date(value)
  if (Number.isNaN(parsed.valueOf())) return fallback

  const elapsed = parsed.valueOf() - Date.now()
  for (const [unit, ms] of UNITS) {
    if (Math.abs(elapsed) >= ms) {
      return RELATIVE.format(Math.round(elapsed / ms), unit)
    }
  }
  return 'just now'
}

/**
 * Short name for an IANA timezone — "EDT" for `America/New_York`.
 *
 * Needed wherever a time is shown that is *not* in the viewer's zone, which on
 * this screen means the scheduled sync times: they fire on the firm's clock, so
 * "3:00 AM" alone would be read as the reader's own 3am. Resolved against the
 * current date on purpose, so the label follows daylight saving rather than
 * claiming EST in July. Returns null for a zone this browser does not know,
 * leaving the caller to show the time unqualified rather than mislabelled.
 */
export function timezoneLabel(zone: string): string | null {
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZone: zone,
      timeZoneName: 'short',
    }).formatToParts(new Date())

    return parts.find((part) => part.type === 'timeZoneName')?.value ?? null
  } catch {
    return null
  }
}

/** Shortens a UUID for display without pretending it is the whole value. */
export function shortId(value: string | null | undefined): string {
  return value ? value.slice(0, 8) : '—'
}

/**
 * Money, formatted through the shared formatter rather than a second one.
 *
 * The API sends integer minor units, so `minor()` is the assertion at the
 * boundary: if a figure ever arrives as a decimal, this throws here rather than
 * rendering a plausible number that is wrong by a factor of a hundred.
 */
export function formatMoney(value: number | null | undefined, fallback = '—'): string {
  if (value === null || value === undefined) return fallback
  return format(minor(value))
}

/**
 * Durations are stored in seconds and read in hours — a timekeeping system's
 * unit is the tenth of an hour, so one decimal place is the useful precision.
 */
export function formatHours(seconds: number | null | undefined, fallback = '—'): string {
  if (seconds === null || seconds === undefined) return fallback
  return `${(seconds / 3600).toFixed(1)} h`
}

const COUNT = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 })

/**
 * Record counts, grouped. A sync run reports five figures at a time and
 * `12480 read` is materially harder to scan than `12,480 read`.
 */
export function formatCount(value: number | null | undefined, fallback = '—'): string {
  return value === null || value === undefined ? fallback : COUNT.format(value)
}

/**
 * Money abbreviated for a chart axis.
 *
 * Lives here rather than beside the one chart that first needed it, because a
 * second chart drawing the same currency on a different scale is how two graphs
 * on one screen come to look like they disagree.
 *
 * The decimal place below ten thousand is not cosmetic: rounding to whole
 * thousands there collapses neighbouring ticks into the same label — a series
 * running from $2,500 to $3,300 rendered "$3k" twice on one axis, which reads
 * as a rendering fault rather than as a scale.
 */
export function formatAxisMoney(value: number): string {
  const dollars = value / 100
  const magnitude = Math.abs(dollars)

  if (magnitude >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}m`
  if (magnitude >= 10_000) return `$${Math.round(dollars / 1_000)}k`
  if (magnitude >= 1_000) return `$${(dollars / 1_000).toFixed(1)}k`

  return `$${Math.round(dollars)}`
}
